import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconBuilding, IconFilterPlus, IconStack2 } from '@tabler/icons-react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  useEcFilterPopoverLayout,
  EC_FILTER_POPOVER_CLASS,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../process-config/components/DefeitosEquipCompatModal.css';
import '../../process-config/components/MarcaSubgruposCadastrosConfig.css';

const EQ2_SPEC_POPOVER_Z = 1302;

const AVATAR_PALETTES = [
  { bg: '#e0f2fe', text: '#0369a1' },
  { bg: '#fef3c7', text: '#b45309' },
  { bg: '#d1fae5', text: '#047857' },
  { bg: '#ffe4e6', text: '#be123c' },
  { bg: '#ffedd5', text: '#c2410c' },
  { bg: '#dbeafe', text: '#1d4ed8' },
];

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function withPopoverStyle(layout) {
  const base = buildEcFilterPopoverStyle(layout);
  if (!base) return null;
  return { ...base, zIndex: EQ2_SPEC_POPOVER_Z };
}

function marcaInitials(nome) {
  const s = String(nome || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function paletteForMarca(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[h];
}

function isTechSelected(included, idBrand, idSubgroup) {
  const bid = String(idBrand);
  if ((included || []).some((it) => it.kind === 'marca' && String(it.idBrand) === bid)) {
    return true;
  }
  return (included || []).some(
    (it) =>
      it.kind === 'sub' &&
      String(it.idBrand) === bid &&
      String(it.idSubgroup) === String(idSubgroup),
  );
}

function Eq2SpecCheckbox({ checked, onClick, title, disabled }) {
  return (
    <label
      className={`deq-check-label${disabled ? ' mscad-check--busy' : ''}`}
      title={title}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="deq-check-native"
        checked={!!checked}
        disabled={!!disabled}
        onChange={() => {
          if (!disabled) onClick?.();
        }}
        onClick={(e) => e.stopPropagation()}
        aria-label={title}
      />
      <span className="deq-check-box" aria-hidden />
    </label>
  );
}

function MarcaAvatar({ fabricante }) {
  const p = paletteForMarca(fabricante);
  return (
    <span className="deq-avatar" style={{ background: p.bg, color: p.text }}>
      {marcaInitials(fabricante)}
    </span>
  );
}

function FiltersEmptyState() {
  return (
    <div className="deq-empty deq-empty--modern">
      <div className="deq-empty-placeholder">
        <IconFilterPlus size={40} stroke={1.25} className="deq-empty-icon" aria-hidden />
        <h3 className="deq-empty-modern-title">Aplique os filtros</h3>
        <p className="deq-empty-modern-desc">
          Escolha fabricante e/ou tecnologia, ou digite parte do nome (mín. 2 caracteres) para listar
          fabricantes e tecnologias disponíveis.
        </p>
      </div>
    </div>
  );
}

function ConfiguredEmptyState() {
  return (
    <div className="deq-empty deq-empty--modern">
      <div className="deq-empty-placeholder">
        <IconStack2 size={40} stroke={1.25} className="deq-empty-icon" aria-hidden />
        <h3 className="deq-empty-modern-title">Nenhuma tecnologia configurada</h3>
        <p className="deq-empty-modern-desc">
          Selecione à esquerda para habilitar as tecnologias deste técnico.
        </p>
      </div>
    </div>
  );
}

export function Equipe2TechDrawerSpecialty({ codigo, baseUrl }) {
  const [catalog, setCatalog] = useState([]);
  const [included, setIncluded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [linkedCollapsed, setLinkedCollapsed] = useState(() => new Set());
  const [fabricantes, setFabricantes] = useState([]);
  const [tecnologias, setTecnologias] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaDeb, setBuscaDeb] = useState('');
  const [fabOpen, setFabOpen] = useState(false);
  const [tecOpen, setTecOpen] = useState(false);
  const [fabSearch, setFabSearch] = useState('');
  const [tecSearch, setTecSearch] = useState('');
  const fabBtnRef = useRef(null);
  const tecBtnRef = useRef(null);
  const fabPopoverLayout = useEcFilterPopoverLayout(fabBtnRef, fabOpen, 238, 320);
  const tecPopoverLayout = useEcFilterPopoverLayout(tecBtnRef, tecOpen, 238, 320);
  const fabPopoverStyle = withPopoverStyle(fabPopoverLayout);
  const tecPopoverStyle = withPopoverStyle(tecPopoverLayout);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/modelos-catalog`, {
          headers: getAuthHeaders(),
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.message || 'Falha ao carregar catálogo');
        if (!cancelled) setCatalog(json.marcas || []);
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Erro ao carregar catálogo');
          setCatalog([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  const loadIncluded = useCallback(async () => {
    if (!codigo) {
      setIncluded([]);
      return;
    }
    try {
      const res = await fetch(
        `${baseUrl}/api/cadastros/tecnicos/gestao/especialidades?codigo=${encodeURIComponent(codigo)}`,
        { headers: getAuthHeaders() },
      );
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Falha ao carregar especialidades');
      setIncluded(json.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar especialidades');
      setIncluded([]);
    }
  }, [baseUrl, codigo]);

  useEffect(() => {
    loadIncluded();
  }, [loadIncluded]);

  useEffect(() => {
    setFabricantes([]);
    setTecnologias([]);
    setBusca('');
    setBuscaDeb('');
    setFabOpen(false);
    setTecOpen(false);
    setFabSearch('');
    setTecSearch('');
    setCollapsed(new Set());
    setLinkedCollapsed(new Set());
  }, [codigo]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 380);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    if (!fabOpen) setFabSearch('');
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) setTecSearch('');
  }, [tecOpen]);

  useEffect(() => {
    if (!fabOpen && !tecOpen) return undefined;
    const onDoc = (e) => {
      if (fabBtnRef.current?.contains(e.target)) return;
      if (tecBtnRef.current?.contains(e.target)) return;
      if (e.target.closest('[data-eq2-spec-fab-popover]')) return;
      if (e.target.closest('[data-eq2-spec-tec-popover]')) return;
      setFabOpen(false);
      setTecOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [fabOpen, tecOpen]);

  const byBrand = useMemo(() => {
    const list = (catalog || []).map((m) => ({
      id_brand: String(m.id),
      brand_nome: m.nome || String(m.id),
      subgrupos: (m.subgrupos || []).map((sg) => ({
        id_subgroup: String(sg.id),
        subgroup_nome: sg.nome || String(sg.id),
        enabled: isTechSelected(included, m.id, sg.id),
      })),
    }));
    list.sort((a, b) => String(a.brand_nome).localeCompare(String(b.brand_nome), 'pt-BR'));
    for (const g of list) {
      g.subgrupos.sort((x, y) =>
        String(x.subgroup_nome).localeCompare(String(y.subgroup_nome), 'pt-BR'),
      );
    }
    return list;
  }, [catalog, included]);

  const marcaItems = useMemo(
    () => byBrand.map((g) => ({ value: String(g.id_brand), label: g.brand_nome })),
    [byBrand],
  );

  const tecnologiaItems = useMemo(() => {
    const names = new Set();
    byBrand.forEach((g) => {
      g.subgrupos.forEach((sg) => {
        const n = String(sg.subgroup_nome || '').trim();
        if (n) names.add(n);
      });
    });
    return [...names]
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((n) => ({ value: n, label: n }));
  }, [byBrand]);

  const filteredFabItems = useMemo(() => {
    const q = fabSearch.trim().toLowerCase();
    if (!q) return marcaItems;
    return marcaItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [marcaItems, fabSearch]);

  const filteredTecItems = useMemo(() => {
    const q = tecSearch.trim().toLowerCase();
    if (!q) return tecnologiaItems;
    return tecnologiaItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [tecnologiaItems, tecSearch]);

  const canShowList =
    fabricantes.length > 0 || tecnologias.length > 0 || buscaDeb.trim().length >= 2;

  const filteredByBrand = useMemo(() => {
    if (!canShowList) return [];
    let list = byBrand;

    if (fabricantes.length > 0) {
      const sel = new Set(fabricantes);
      list = list.filter((g) => sel.has(String(g.id_brand)));
    }

    if (tecnologias.length > 0) {
      const sel = new Set(tecnologias);
      list = list
        .map((g) => ({
          ...g,
          subgrupos: g.subgrupos.filter((s) => sel.has(String(s.subgroup_nome || '').trim())),
        }))
        .filter((g) => g.subgrupos.length > 0);
    }

    const q = buscaDeb.trim().toLowerCase();
    if (q.length >= 2) {
      list = list
        .map((g) => {
          const brandMatch =
            String(g.brand_nome || '').toLowerCase().includes(q) ||
            String(g.id_brand || '').toLowerCase().includes(q);
          const subgrupos = brandMatch
            ? g.subgrupos
            : g.subgrupos.filter(
                (s) =>
                  String(s.subgroup_nome || '').toLowerCase().includes(q) ||
                  String(s.id_subgroup || '').toLowerCase().includes(q),
              );
          if (!brandMatch && subgrupos.length === 0) return null;
          return { ...g, subgrupos };
        })
        .filter(Boolean);
    }

    return list;
  }, [byBrand, fabricantes, tecnologias, buscaDeb, canShowList]);

  const enabledByBrand = useMemo(
    () =>
      byBrand
        .map((g) => ({
          ...g,
          subgrupos: g.subgrupos.filter((s) => s.enabled),
        }))
        .filter((g) => g.subgrupos.length > 0),
    [byBrand],
  );

  const enabledCount = useMemo(
    () => enabledByBrand.reduce((n, g) => n + g.subgrupos.length, 0),
    [enabledByBrand],
  );

  const hasFilters = fabricantes.length > 0 || tecnologias.length > 0 || busca !== '';

  useEffect(() => {
    if (loading || !canShowList) return;
    setCollapsed((prev) => {
      const next = new Set(prev);
      filteredByBrand.forEach((g) => next.delete(g.brand_nome));
      return next;
    });
  }, [loading, canShowList, filteredByBrand]);

  const rowKey = (idBrand, idSub) => `${idBrand}::${idSub}`;

  const setEnabled = async (idBrand, idSubgroup, enabled) => {
    if (!codigo || busyKey) return;
    const pk = rowKey(idBrand, idSubgroup);
    setBusyKey(pk);
    setErr(null);
    try {
      const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/especialidades`, {
        method: enabled ? 'POST' : 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codigo, idBrand, idSubgroup }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Não foi possível atualizar especialidade');
      }
      setIncluded(json.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao atualizar');
    } finally {
      setBusyKey(null);
    }
  };

  const toggleGroupSelection = async (g) => {
    if (!codigo || busyKey) return;
    const allEnabled = g.subgrupos.every((s) => s.enabled);
    const targets = allEnabled
      ? g.subgrupos.filter((s) => s.enabled)
      : g.subgrupos.filter((s) => !s.enabled);
    setBusyKey(`brand:${g.id_brand}`);
    setErr(null);
    try {
      let lastItems = included;
      for (const s of targets) {
        const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/especialidades`, {
          method: allEnabled ? 'DELETE' : 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ codigo, idBrand: g.id_brand, idSubgroup: s.id_subgroup }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Não foi possível atualizar especialidade');
        }
        lastItems = json.items || [];
      }
      setIncluded(lastItems);
    } catch (e) {
      setErr(e.message || 'Erro ao atualizar');
      await loadIncluded();
    } finally {
      setBusyKey(null);
    }
  };

  const clearBrandEnabled = async (g) => {
    if (!codigo || busyKey) return;
    const targets = g.subgrupos.filter((s) => s.enabled);
    if (!targets.length) return;
    setBusyKey(`brand:${g.id_brand}`);
    setErr(null);
    try {
      let lastItems = included;
      for (const s of targets) {
        const res = await fetch(`${baseUrl}/api/cadastros/tecnicos/gestao/especialidades`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ codigo, idBrand: g.id_brand, idSubgroup: s.id_subgroup }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Não foi possível atualizar especialidade');
        }
        lastItems = json.items || [];
      }
      setIncluded(lastItems);
    } catch (e) {
      setErr(e.message || 'Erro ao atualizar');
      await loadIncluded();
    } finally {
      setBusyKey(null);
    }
  };

  const toggleGroup = (fab) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  };

  const toggleLinkedGroup = (fab) => {
    setLinkedCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  };

  const handleToggleFab = (value) => {
    setFabricantes((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value],
    );
  };

  const handleFabBulkToggle = () => {
    const visible = filteredFabItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => fabricantes.includes(v));
    setFabricantes((prev) =>
      allSelected
        ? prev.filter((m) => !visible.includes(m))
        : Array.from(new Set([...prev, ...visible])),
    );
  };

  const handleToggleTec = (value) => {
    setTecnologias((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  };

  const handleTecBulkToggle = () => {
    const visible = filteredTecItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => tecnologias.includes(v));
    setTecnologias((prev) =>
      allSelected
        ? prev.filter((t) => !visible.includes(t))
        : Array.from(new Set([...prev, ...visible])),
    );
  };

  const clearFilters = () => {
    setFabricantes([]);
    setTecnologias([]);
    setBusca('');
    setFabSearch('');
    setTecSearch('');
  };

  if (loading) {
    return <p className="eq2-tdp-muted">Carregando especialidades…</p>;
  }

  return (
    <div className="mscad-equip-panel eq2-tdp-spec-equip">
      {err ? <p className="eq2-tdp-err eq2-tdp-spec-status">{err}</p> : null}

      <div className="deq-filters mscad-filters">
        <EcClickUpFilterChip
          anchorRef={fabBtnRef}
          label="Fabricante"
          value={fabricantes.length > 0 ? String(fabricantes.length) : null}
          icon={IconBuilding}
          hasSelection={fabricantes.length > 0}
          isOpen={fabOpen}
          ariaLabel="Filtrar por fabricante"
          onClick={() => {
            setTecOpen(false);
            setFabOpen((o) => !o);
          }}
        />
        {fabOpen && fabPopoverStyle
          ? ReactDOM.createPortal(
              <div
                data-eq2-spec-fab-popover
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                style={fabPopoverStyle}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar fabricante..."
                  searchTerm={fabSearch}
                  onSearchChange={setFabSearch}
                  items={filteredFabItems}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={fabricantes}
                  onToggle={(item) => handleToggleFab(item.value)}
                  emptyMessage="Nenhum fabricante"
                  listAriaLabel="Fabricantes"
                  bulkMenuEnabled
                  onBulkToggleAll={handleFabBulkToggle}
                />
              </div>,
              document.body,
            )
          : null}

        <EcClickUpFilterChip
          anchorRef={tecBtnRef}
          label="Tecnologia"
          value={tecnologias.length > 0 ? String(tecnologias.length) : null}
          icon={IconStack2}
          hasSelection={tecnologias.length > 0}
          isOpen={tecOpen}
          ariaLabel="Filtrar por tecnologia"
          onClick={() => {
            setFabOpen(false);
            setTecOpen((o) => !o);
          }}
        />
        {tecOpen && tecPopoverStyle
          ? ReactDOM.createPortal(
              <div
                data-eq2-spec-tec-popover
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                style={tecPopoverStyle}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar tecnologia..."
                  searchTerm={tecSearch}
                  onSearchChange={setTecSearch}
                  items={filteredTecItems}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={tecnologias}
                  onToggle={(item) => handleToggleTec(item.value)}
                  emptyMessage="Nenhuma tecnologia"
                  listAriaLabel="Tecnologias"
                  bulkMenuEnabled
                  onBulkToggleAll={handleTecBulkToggle}
                />
              </div>,
              document.body,
            )
          : null}

        <BoardToolbarExpandSearch
          value={busca}
          onChange={setBusca}
          placeholder="Buscar fabricante ou tecnologia..."
          ariaLabel="Buscar fabricante ou tecnologia"
          forceExpanded
        />

        {hasFilters ? (
          <button type="button" className="deq-clear-filters" onClick={clearFilters}>
            Limpar filtros
          </button>
        ) : null}

        {canShowList ? (
          <span className="deq-filter-count">
            {filteredByBrand.reduce((n, g) => n + g.subgrupos.length, 0)} tecnologias
          </span>
        ) : null}
      </div>

      <div className="deq-main mscad-main">
        <div className="deq-available mscad-available">
          <div className="deq-scroll mscad-scroll mscad-scroll--available">
            {!canShowList ? (
              <FiltersEmptyState />
            ) : filteredByBrand.length === 0 ? (
              <div className="deq-empty">
                <div className="deq-empty-title">Nenhum resultado encontrado</div>
                <div className="deq-empty-hint">Ajuste os filtros para ver mais resultados.</div>
              </div>
            ) : (
              filteredByBrand.map((g) => {
                const fab = g.brand_nome;
                const isCollapsed = collapsed.has(fab);
                const enabledInBrand = g.subgrupos.filter((s) => s.enabled).length;
                const allFabEnabled = g.subgrupos.every((s) => s.enabled);
                const brandBusy =
                  busyKey === `brand:${g.id_brand}` ||
                  g.subgrupos.some((s) => busyKey === rowKey(g.id_brand, s.id_subgroup));

                return (
                  <div key={g.id_brand}>
                    <button type="button" className="deq-brand-head" onClick={() => toggleGroup(fab)}>
                      <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                        {isCollapsed ? (
                          <ChevronRight size={13} strokeWidth={2} />
                        ) : (
                          <ChevronDown size={13} strokeWidth={2} />
                        )}
                      </span>
                      <MarcaAvatar fabricante={fab} />
                      <span className="deq-brand-label">{fab}</span>
                      <span className="deq-brand-meta">
                        {g.subgrupos.length}
                        {enabledInBrand > 0 ? ` · ${enabledInBrand} hab.` : ''}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="deq-brand-sel-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!brandBusy) toggleGroupSelection(g);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!brandBusy) toggleGroupSelection(g);
                          }
                        }}
                      >
                        {allFabEnabled ? 'rem. todos' : 'sel. todos'}
                      </span>
                    </button>

                    {!isCollapsed
                      ? g.subgrupos.map((s) => {
                          const pk = rowKey(g.id_brand, s.id_subgroup);
                          const busy = busyKey === pk || busyKey === `brand:${g.id_brand}`;
                          return (
                            <div
                              key={pk}
                              className={`deq-model-row${s.enabled ? ' deq-model-row--linked' : ''}`}
                              onClick={() => !busy && setEnabled(g.id_brand, s.id_subgroup, !s.enabled)}
                            >
                              <Eq2SpecCheckbox
                                checked={s.enabled}
                                disabled={busy}
                                onClick={() => setEnabled(g.id_brand, s.id_subgroup, !s.enabled)}
                                title={`Habilitar ${s.subgroup_nome}`}
                              />
                              <span className="deq-model-name" title={s.subgroup_nome}>
                                {s.subgroup_nome}
                              </span>
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="deq-linked-panel mscad-linked-panel">
          <div className="deq-linked-head">
            <span className="deq-linked-title">Configurados · {enabledCount}</span>
          </div>
          <div className="deq-scroll mscad-scroll mscad-scroll--linked">
            {enabledByBrand.length === 0 ? (
              <ConfiguredEmptyState />
            ) : (
              enabledByBrand.map((g) => {
                const fab = g.brand_nome;
                const isLinkedCollapsed = linkedCollapsed.has(fab);
                const brandBusy =
                  busyKey === `brand:${g.id_brand}` ||
                  g.subgrupos.some((s) => busyKey === rowKey(g.id_brand, s.id_subgroup));
                return (
                  <div key={`linked-${g.id_brand}`}>
                    <button
                      type="button"
                      className="deq-brand-head deq-brand-head--linked"
                      onClick={() => toggleLinkedGroup(fab)}
                    >
                      <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                        {isLinkedCollapsed ? (
                          <ChevronRight size={13} strokeWidth={2} />
                        ) : (
                          <ChevronDown size={13} strokeWidth={2} />
                        )}
                      </span>
                      <MarcaAvatar fabricante={fab} />
                      <span className="deq-brand-label">{fab}</span>
                      <span className="deq-brand-meta">{g.subgrupos.length}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="mscad-brand-clear-all"
                        title="Remover todas as tecnologias deste fabricante"
                        aria-label={`Remover todas as tecnologias de ${fab}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!brandBusy) clearBrandEnabled(g);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!brandBusy) clearBrandEnabled(g);
                          }
                        }}
                      >
                        <X size={13} strokeWidth={2} aria-hidden />
                      </span>
                    </button>
                    {!isLinkedCollapsed
                      ? g.subgrupos.map((s) => {
                          const pk = rowKey(g.id_brand, s.id_subgroup);
                          const busy = busyKey === pk || busyKey === `brand:${g.id_brand}`;
                          return (
                            <div
                              key={pk}
                              className="deq-linked-row"
                              onClick={() => !busy && setEnabled(g.id_brand, s.id_subgroup, false)}
                            >
                              <span className="deq-linked-name" title={s.subgroup_nome}>
                                {s.subgroup_nome}
                              </span>
                              <X className="deq-linked-remove" size={13} strokeWidth={2} aria-hidden />
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Equipe2TechDrawerSpecialty;
